import type { ApiRequestHelpers } from "./useHttpClient";
import { useCurrentLocation } from "#imports";
import type {
  AddressLookupParams,
  AddressLookupResult,
  CovResponse,
  ExactDivision,
  ExactGLAccount,
  ExactItemGroup,
  ExactJournal,
  ExactVatCode,
  PatientIdentifier,
  Response,
  SaveCompanySettingResponse,
  SbvzPatient,
  TwinfieldGeneralLedgerAccount,
  TwinfieldOffice,
  TwinfieldTransactionType,
  TwinfieldVatCode,
} from "~/types/api";
import type { CountryOption } from "~/types/support-options";

export type SbvzSearchRequest =
  | {
      method: "address";
      birth_date: string;
      gender: string;
      postal_code: string;
      housenumber: string;
      housenumber_addition?: string;
    }
  | {
      method: "bsn";
      birth_date: string;
      gender: string;
      bsn: string;
    }
  | {
      birth_date: string;
      gender: string;
      surname: string;
    };

export function createExternalApi({ getRequest, postRequest, deleteRequest }: ApiRequestHelpers) {
  const { currentLocationId } = useCurrentLocation();

  /** Searches the SBV-Z registry for patient identity data by address, BSN, or surname. */
  const sbvZSearch = (data: SbvzSearchRequest) =>
    postRequest<Response<SbvzPatient[]>>("/patients/sbv-z", {
      method: "method" in data ? data.method : undefined,
      postal_code: "postal_code" in data ? data.postal_code : undefined,
      housenumber: "housenumber" in data ? data.housenumber : undefined,
      housenumber_addition: "housenumber_addition" in data ? data.housenumber_addition : undefined,
      gender: data.gender,
      birth_date: data.birth_date,
      surname: "surname" in data ? data.surname : undefined,
      bsn: "bsn" in data ? data.bsn : undefined,
    });

  /** Fetches the list of supported countries for patient/address forms. */
  const referenceCountries = () =>
    getRequest<Response<CountryOption[]>>("/support/countries").then((response) => response.data);

  const lookupAddress = (params: AddressLookupParams) =>
    getRequest<Response<AddressLookupResult>>("/addresses/lookup", { params, suppressValidationToast: true });

  /** Collects COV insurance data for a patient invitation. */
  const collectCovData = (data: {
    birth_date: string;
    sex: string;
    first_name?: string;
    last_name?: string;
    citizen_service_number?: string;
  }) =>
    postRequest<CovResponse[]>(`/locations/${currentLocationId.value}/patient-invitations/collect-cov-data`, {
      bsn: data.citizen_service_number,
      dob: data.birth_date,
      gender: data.sex,
      first_name: data.first_name,
      last_name: data.last_name,
    });

  /** Refreshes COV insurance data for an existing patient. */
  const updateCovData = (patientId: PatientIdentifier) => getRequest<CovResponse[]>(`/patients/${patientId}/cov`);

  /** Stores OAuth2 credentials for an external integration provider. */
  const storeOAuth2Integration = (provider: string, data: {}) =>
    postRequest<SaveCompanySettingResponse>("/company-settings/oauth2/" + provider, data);

  /** Activates an external integration by type. */
  const activateIntegration = (type: string) =>
    postRequest<SaveCompanySettingResponse>("/company-settings/integration/" + type);

  /** Deactivates an external integration by type. */
  const deactivateIntegration = (type: string) =>
    deleteRequest<SaveCompanySettingResponse>("/company-settings/integration/" + type);

  /** Fetches Twinfield transaction types available to the current organization. */
  const getTwinfieldTransactionTypes = () =>
    getRequest<Response<TwinfieldTransactionType[]>>("/company-settings/integration/twinfield/transaction-types");

  /** Fetches Twinfield offices available to the current organization. */
  const getTwinfieldOffices = () =>
    getRequest<Response<TwinfieldOffice[]>>("/company-settings/integration/twinfield/offices");

  /** Fetches Twinfield general ledger accounts filtered by dimension type. */
  const getTwinfieldGeneralLedgerAccounts = (dimtype: "PNL" | "BAS") =>
    getRequest<Response<TwinfieldGeneralLedgerAccount[]>>(
      `/company-settings/integration/twinfield/general-ledger-accounts?dimtype=${dimtype}`,
    );

  /** Fetches Twinfield VAT codes available to the current organization. */
  const getTwinfieldVatCodes = () =>
    getRequest<Response<TwinfieldVatCode[]>>("/company-settings/integration/twinfield/vat-codes");

  /** Fetches Exact Online divisions available to the current organization. */
  const getExactDivisions = () =>
    getRequest<Response<ExactDivision[]>>("/company-settings/integration/exact/divisions");

  /** Fetches Exact Online GL accounts. */
  const getExactGLAccounts = () =>
    getRequest<Response<ExactGLAccount[]>>("/company-settings/integration/exact/gl-accounts");

  /** Fetches Exact Online item groups. */
  const getExactItemGroups = () =>
    getRequest<Response<ExactItemGroup[]>>("/company-settings/integration/exact/item-groups");

  /** Fetches Exact Online journals. */
  const getExactJournals = () => getRequest<Response<ExactJournal[]>>("/company-settings/integration/exact/journals");

  /** Fetches Exact Online VAT codes. */
  const getExactVatCodes = () => getRequest<Response<ExactVatCode[]>>("/company-settings/integration/exact/vat-codes");

  return {
    sbvZSearch,
    referenceCountries,
    lookupAddress,
    collectCovData,
    updateCovData,
    storeOAuth2Integration,
    activateIntegration,
    deactivateIntegration,
    getTwinfieldTransactionTypes,
    getTwinfieldOffices,
    getTwinfieldGeneralLedgerAccounts,
    getTwinfieldVatCodes,
    getExactDivisions,
    getExactGLAccounts,
    getExactItemGroups,
    getExactJournals,
    getExactVatCodes,
  };
}

export type ExternalApi = ReturnType<typeof createExternalApi>;
